/**
 * OverviewHelpers — shared primitives used by HorseOverview and AccountOverview.
 *
 * Exported:
 *  - SectionHeader      — collapsible section title bar with optional Add action
 *  - TaskStatusIndicator — Lucide icon sized to the task status
 */
import React from 'react';
import { Button } from '../ui/button';
import {
  ChevronDown, ChevronRight,
  Circle, Clock, PauseCircle, CheckCircle2, XCircle,
  Plus,
} from 'lucide-react';

// ── SectionHeader ─────────────────────────────────────────────────────────────
/**
 * @param {string}   title      — section label (UPPERCASE rendered by CSS)
 * @param {React.ElementType} [icon] — optional Lucide icon component
 * @param {boolean}  isOpen     — controls chevron direction
 * @param {function} onToggle   — called when the row is clicked
 * @param {function} [action]   — if present, renders an "Add" button
 */
export const SectionHeader = ({ title, icon: Icon, action, isOpen, onToggle }) => (
  <div
    className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/30 rounded px-2"
    onClick={onToggle}
    data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-2">
      {onToggle && (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
      {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
      <h3 className="font-semibold text-sm uppercase tracking-wide">{title}</h3>
    </div>
    {action && (
      <Button
        variant="ghost"
        size="sm"
        className="h-6"
        onClick={(e) => { e.stopPropagation(); action(); }}
        data-testid={`add-${title.toLowerCase().replace(/\s+/g, '-')}-btn`}
      >
        <Plus className="w-3 h-3 mr-1" /> Add
      </Button>
    )}
  </div>
);

// ── TaskStatusIndicator ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
  not_started: { Icon: Circle,      className: 'text-gray-400'   },
  in_progress: { Icon: Clock,       className: 'text-blue-500'   },
  on_hold:     { Icon: PauseCircle, className: 'text-yellow-500' },
  completed:   { Icon: CheckCircle2,className: 'text-green-500'  },
  cancelled:   { Icon: XCircle,     className: 'text-red-500'    },
};

/**
 * @param {string} status — task_status value
 * @param {'sm'|'md'|'lg'} [size='md']
 */
export const TaskStatusIndicator = ({ status, size = 'md' }) => {
  const sizeClass = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' }[size] ?? 'w-5 h-5';
  const { Icon, className } = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_started;
  return <Icon className={`${sizeClass} ${className}`} />;
};
